/* eslint-disable max-statements, max-lines-per-function */
import { Button, Group, LoadingOverlay, Stack, Switch, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { z } from 'zod';
import { useEffect, useMemo, useState } from 'react';

import { Customer, Declarant, SortDirection, Template, TemplateType, TSDType, User } from '@e-origin/shared';

import { useAdminImportTsd, useAdminTemplates, useCustomers, useDeclarants } from '@admin/hooks';
import { useAdminUsers } from '@admin/hooks/users/useAdminUsers';
import { Select, UploadFile } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';

interface ImportTsdProps {
  onClose: () => void;
  tsdView: TSDType;
}

const importTsdSchema = z.object({
  customer: z.string().min(1, { message: 'Required' }),
  template: z.string().min(1, { message: 'Required' }),
  declarant: z.string().min(1, { message: 'Required' }),
  user: z.string().min(1, { message: 'Required' }),
  resetCustomsData: z.boolean(),
  file: z.instanceof(File),
});

type ImportTsdValues = z.infer<typeof importTsdSchema>;

const getTemplateType = (tsdView: TSDType) => {
  switch (tsdView) {
    case TSDType.PRELODGED:
      return TemplateType.PRELODGED_TSD;
    case TSDType.COMBINED:
      return TemplateType.COMBINED_TSD;
    case TSDType.TRANSFERRED:
      return TemplateType.TRANSFERRED_TSD;
    default:
      return TemplateType.COMBINED_TSD;
  }
};

export const ImportTsd: React.FC<ImportTsdProps> = ({ onClose, tsdView }) => {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedDeclarant, setSelectedDeclarant] = useState<string | null>(null);
  const importTsdMutation = useAdminImportTsd();

  const { data: customersList = [], isLoading: customersLoading } = useCustomers();
  const { data: declarantsList, isLoading: declarantsLoading } = useDeclarants();

  const { data: usersData, isLoading: usersLoading } = useAdminUsers({
    filters: {},
    pagination: { page: 1, size: 1000 },
    sorting: { field: 'email', direction: SortDirection.ASC },
  });

  const templateType = useMemo(() => getTemplateType(tsdView), [tsdView]);

  const { data: { list: templatesList = [] } = {}, isLoading: templatesLoading } = useAdminTemplates({
    filters: { type: templateType },
    pagination: { page: 1, size: 1000 },
    sorting: { field: 'name', direction: SortDirection.ASC },
  });

  const allUsers = usersData?.data?.users || [];

  const filteredUsers = useMemo(() => {
    if (!selectedDeclarant) return [];

    return allUsers.filter((user) => (user.declarant as unknown as Declarant)?._id === selectedDeclarant);
  }, [allUsers, selectedDeclarant]);

  useEffect(() => {
    if (!customersList.length) {
      setErrorMessage('No customers available.');
    } else if (!templatesList.length) {
      setErrorMessage('No templates available.');
    } else if (!declarantsList.length) {
      setErrorMessage('No declarants available.');
    } else if (!usersData?.data?.users?.length) {
      setErrorMessage('No users available.');
    } else {
      setErrorMessage(null);
    }
  }, [customersList, templatesList, declarantsList, usersData]);

  const form = useFormWithNameInputProp<ImportTsdValues>({
    initialValues: {
      customer: '',
      template: '',
      declarant: '',
      user: '',
      resetCustomsData: false,
      file: null,
    },
    validate: zodResolver(importTsdSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    const declarantValue = form.values.declarant;

    if (declarantValue !== selectedDeclarant) {
      setSelectedDeclarant(declarantValue);
      form.setFieldValue('user', '');
    }
  }, [form.values.declarant]);

  const customerOptions = customersList.map((customer: Customer) => ({
    label: customer.name || '',
    value: customer._id || '',
  }));

  const templateOptions = templatesList.map((template: Template) => ({
    label: template.name || '',
    value: template._id || '',
  }));

  const declarantOptions =
    declarantsList?.map((declarant: Declarant) => ({
      label: declarant.accountName || '',
      value: declarant._id || '',
    })) || [];

  const userOptions = useMemo(() => {
    return filteredUsers.map((user: User) => ({
      label: user.email || '',
      value: user._id || '',
    }));
  }, [filteredUsers]);

  const onSubmit = async (values: ImportTsdValues) => {
    setErrorMessage(null);
    setSubmitting(true);

    try {
      await importTsdMutation.mutateAsync(
        {
          customerId: values.customer,
          templateId: values.template,
          declarantId: values.declarant,
          userId: values.user,
          resetCustomsData: values.resetCustomsData,
          file: values.file,
        },
        {
          onSuccess: () => {
            onClose();
          },
        },
      );
    } catch (error) {
      setErrorMessage((error as any).response?.data?.message ?? 'An error occurred while importing the TSD');
    } finally {
      setSubmitting(false);
    }
  };

  if (customersLoading || templatesLoading || declarantsLoading || usersLoading) {
    return <LoadingOverlay visible overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm" mb="xl">
        <Select {...form.getInputProps('customer')} label="Select customer" data={customerOptions} searchable size="lg" />
        <Select {...form.getInputProps('template')} label="Select template" data={templateOptions} searchable size="lg" />
        <Select {...form.getInputProps('declarant')} label="Select declarant" data={declarantOptions} searchable size="lg" />
        <Select {...form.getInputProps('user')} label="Select user" data={userOptions} searchable size="lg" disabled={!selectedDeclarant} />

        <Switch {...form.getInputProps('resetCustomsData')} my="sm" w="fit-content" checked={form.values.resetCustomsData} label="Reset customs data" />

        <UploadFile {...form.getInputProps('file')} isMulti={false} type="zip" p="xs" />

        {errorMessage && (
          <Text c="red" fw="bold">
            {errorMessage}
          </Text>
        )}
      </Stack>

      <Group justify="center">
        <Button type="button" variant="transparent" onClick={onClose} disabled={submitting} size="lg">
          Cancel
        </Button>

        <Button type="submit" loading={submitting} disabled={!form.isValid()} variant="filled" size="lg">
          Import
        </Button>
      </Group>
    </form>
  );
};
